import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecoverPage() {
  const { token } = useParams();
  const { replaceCart } = useStore();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | ok | gone | notfound | error
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/cart/recover/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        if (cancelled) return;
        const lines = data.items || [];
        if (lines.length === 0) {
          setState("gone");
          return;
        }
        replaceCart(lines);
        setItems(lines);
        setState("ok");
        toast.success("CART RESTORED. PICK UP WHERE YOU LEFT OFF.");
        // Give the user a beat to see what happened, then push to checkout
        setTimeout(() => {
          if (!cancelled) navigate("/checkout", { replace: true });
        }, 1800);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) setState("notfound");
        else if (status === 410) setState("gone");
        else setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [token, replaceCart, navigate]);

  return (
    <div
      data-testid="recover-page"
      className="pt-[140px] pb-24 px-6 min-h-[80vh] flex items-center justify-center"
    >
      <div className="max-w-xl w-full text-center">
        {state === "loading" && (
          <>
            <div className="font-mono text-xs uppercase tracking-[0.16em] text-ink/60 mb-4">
              Recovering your cart
            </div>
            <h1 className="font-display uppercase text-5xl sm:text-6xl mb-4">Hold on.</h1>
            <p className="font-body text-lg text-ink/70">
              Pulling the piece you left behind.
            </p>
          </>
        )}

        {state === "ok" && (
          <>
            <div className="font-mono text-xs uppercase tracking-[0.16em] text-red mb-4">
              Cart restored
            </div>
            <h1 className="font-display uppercase text-5xl sm:text-6xl mb-4">
              Pick up where you stopped.
            </h1>
            <p className="font-body text-lg text-ink/70 mb-6">
              {items.length} {items.length === 1 ? "piece" : "pieces"} back in your cart.
              Sending you to checkout.
            </p>
            <Link
              data-testid="recover-go-checkout"
              to="/checkout"
              className="inline-block bg-ink text-cream px-7 py-3 font-display uppercase tracking-[0.14em] text-sm hover:bg-red transition-colors"
            >
              Finish what you started
            </Link>
          </>
        )}

        {state === "gone" && (
          <>
            <div className="font-mono text-xs uppercase tracking-[0.16em] text-ink/60 mb-4">
              Recovery link expired
            </div>
            <h1 className="font-display uppercase text-5xl sm:text-6xl mb-4">
              That window closed.
            </h1>
            <p className="font-body text-lg text-ink/70 mb-6">
              Either the order is already paid, was cancelled, or the link has aged out.
              The shop is still wide open.
            </p>
            <Link
              data-testid="recover-go-shop"
              to="/shop"
              className="inline-block bg-ink text-cream px-7 py-3 font-display uppercase tracking-[0.14em] text-sm hover:bg-red transition-colors"
            >
              Browse the drop
            </Link>
          </>
        )}

        {state === "notfound" && (
          <>
            <div className="font-mono text-xs uppercase tracking-[0.16em] text-ink/60 mb-4">
              Link not recognized
            </div>
            <h1 className="font-display uppercase text-5xl sm:text-6xl mb-4">
              We don't have that one.
            </h1>
            <p className="font-body text-lg text-ink/70 mb-6">
              This recovery link does not match any pending order. Go grab something
              fresh.
            </p>
            <Link
              to="/shop"
              className="inline-block bg-ink text-cream px-7 py-3 font-display uppercase tracking-[0.14em] text-sm hover:bg-red transition-colors"
            >
              Shop now
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <h1 className="font-display uppercase text-5xl sm:text-6xl mb-4">
              Something tore.
            </h1>
            <p className="font-body text-lg text-ink/70 mb-6">
              We could not load your cart right now. Try again in a minute.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-block bg-ink text-cream px-7 py-3 font-display uppercase tracking-[0.14em] text-sm hover:bg-red transition-colors"
            >
              Retry
            </button>
          </>
        )}
      </div>
    </div>
  );
}
